/**
 * Status pill — the app-wide "state at a glance" chip.
 *
 * A compact, softly tinted badge with a subtle status edge and an indicator
 * dot in the full-strength status color next to a short label. Colors come
 * from the semantic status palette in cssTokens.js
 * (--status-<family>, -text, -tint, -border), so a chip, a row dot, and a map marker
 * showing the same state can never drift apart, and dark mode / tenant
 * reseeding apply for free. Grid columns render it via GridStatusCell
 * (@sophocles/data-grids); non-grid surfaces use this directly.
 */
import { Box } from '@mui/material';
import React from 'react';

const STATUS_FAMILIES = ['ok', 'warning', 'bad', 'info', 'attention', 'neutral'];

/**
 * @param {object} props
 * @param {React.ReactNode} props.label - Short status text (already translated).
 * @param {'ok'|'warning'|'bad'|'info'|'attention'|'neutral'} [props.color]
 *   Status-palette family for the tint/dot/text triple (see cssTokens.js);
 *   'attention' is the purple "needs a human" state. Unknown values render
 *   neutral.
 * @param {boolean} [props.dot] - Show the indicator dot; AlarmChip turns it off.
 * @param {object} [props.sx] - Extra sx merged onto the pill.
 *   Remaining props (onClick, aria-*, …) and the ref forward to the pill
 *   element, so Tooltip can wrap it directly.
 */
const StatusChip = React.forwardRef(function StatusChip(
  { label, color = 'neutral', dot = true, sx = {}, ...rest },
  ref,
) {
  const family = STATUS_FAMILIES.includes(color) ? color : 'neutral';

  return (
    <Box
      ref={ref}
      {...rest}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--status-chip-gap)',
        height: 'var(--status-chip-height)',
        px: 'var(--status-chip-px)',
        border: '1px solid',
        borderColor: `var(--status-${family}-border)`,
        borderRadius: 'var(--status-chip-radius)',
        bgcolor: `var(--status-${family}-tint)`,
        color: `var(--status-${family}-text)`,
        fontSize: 'var(--status-chip-font-size)',
        lineHeight: 1,
        whiteSpace: 'nowrap',
        fontWeight: 600,
        ...sx,
      }}
    >
      {dot && (
        <Box
          component="span"
          sx={{
            width: 'var(--status-chip-dot-size)',
            height: 'var(--status-chip-dot-size)',
            borderRadius: '50%',
            bgcolor: `var(--status-${family})`,
            flexShrink: 0,
          }}
        />
      )}
      {label}
    </Box>
  );
});

export default StatusChip;
